import { memo, type ReactNode } from "react";
import { Modal, Pressable, ViewStyle, StyleProp } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";

interface DialogModalProps {
  visible: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  title: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}

export const DialogModal = memo(
  ({ visible, onClose, showCloseButton = true, title, children, style }: DialogModalProps) => {
    const { theme } = useUnistyles();

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <Pressable style={styles.backdrop} onPress={onClose}>
          <Pressable
            style={[styles.dialog, { backgroundColor: theme.colors.surface }, style]}
            onPress={(e) => e.stopPropagation()}
          >
            <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="3xl">
              <Text textStyle="md/semibold">{title}</Text>
              {showCloseButton && (
                <Pressable onPress={onClose}>
                  <View
                    height={28}
                    width={28}
                    alignItems="center"
                    justifyContent="center"
                    borderWidth={1}
                    borderColor="borderCard"
                    rounded={theme.spacing.md}
                  >
                    <Icon name="close" size={20} color={theme.colors.icon} />
                  </View>
                </Pressable>
              )}
            </View>
            {children}
          </Pressable>
        </Pressable>
      </Modal>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: theme.spacing["5xl"],
  },
  dialog: {
    width: "100%",
    borderRadius: theme.spacing.md,
    paddingHorizontal: theme.spacing["6xl"],
    paddingVertical: theme.spacing["8xl"],
  },
}));
